/**
 * Cookie Consent Banner
 *
 * GDPR-compliant cookie consent component for analytics tracking.
 * Stores user preference in localStorage and integrates with analytics.
 * Variants by path: main-site theme (default, near-black with amber accent),
 * Cosmos violet theme (/cosmos), Realtor light theme (/cosmos/realtor).
 *
 * @version 1.1.0 - Added Cosmos theme auto-detection
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Settings } from 'lucide-react';
import { setAnalyticsConsent, hasAnalyticsConsent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

const CONSENT_KEY = 'cookie_consent_status';
const CONSENT_TIMESTAMP_KEY = 'cookie_consent_timestamp';

type ConsentStatus = 'pending' | 'accepted' | 'declined' | 'custom';

interface ConsentPreferences {
  necessary: boolean; // Always true
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

export interface CookieConsentProps {
  /** Privacy policy URL */
  privacyPolicyUrl?: string;
  /** Cookie policy URL */
  cookiePolicyUrl?: string;
  /** Position of the banner */
  position?: 'bottom' | 'bottom-left' | 'bottom-right' | 'top';
  /** Custom class name */
  className?: string;
  /** Callback when consent is given */
  onConsent?: (preferences: ConsentPreferences) => void;
  /** Show preferences button */
  showPreferencesButton?: boolean;
  /** Days until consent expires */
  consentExpiryDays?: number;
}

/**
 * Cookie Consent Banner Component
 *
 * @example
 * ```tsx
 * <CookieConsent
 *   privacyPolicyUrl="/privacy"
 *   position="bottom"
 *   onConsent={(prefs) => console.log('Consent:', prefs)}
 * />
 * ```
 */
export function CookieConsent({
  privacyPolicyUrl = '/privacy',
  cookiePolicyUrl,
  position = 'bottom',
  className,
  onConsent,
  showPreferencesButton = true,
  consentExpiryDays = 365,
}: CookieConsentProps): JSX.Element | null {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true,
    analytics: true,
    marketing: false,
    preferences: true,
  });

  // Check for existing consent on mount
  useEffect(() => {
    const status = localStorage.getItem(CONSENT_KEY);
    const timestamp = localStorage.getItem(CONSENT_TIMESTAMP_KEY);

    // Check if consent has expired
    if (timestamp) {
      const consentDate = new Date(timestamp);
      const expiryDate = new Date(consentDate);
      expiryDate.setDate(expiryDate.getDate() + consentExpiryDays);

      if (new Date() > expiryDate) {
        // Consent has expired, clear and show banner
        localStorage.removeItem(CONSENT_KEY);
        localStorage.removeItem(CONSENT_TIMESTAMP_KEY);
        setIsVisible(true);
        return;
      }
    }

    if (!status) {
      // No consent recorded, show banner
      setIsVisible(true);
    } else if (status === 'accepted' || status === 'custom') {
      // Apply saved preferences
      const savedPrefs = localStorage.getItem('cookie_preferences');
      if (savedPrefs) {
        const parsedPrefs = JSON.parse(savedPrefs) as ConsentPreferences;
        setPreferences(parsedPrefs);
        setAnalyticsConsent(parsedPrefs.analytics);
      } else {
        setAnalyticsConsent(true);
      }
    }
  }, [consentExpiryDays]);

  const saveConsent = useCallback(
    (status: ConsentStatus, prefs: ConsentPreferences) => {
      localStorage.setItem(CONSENT_KEY, status);
      localStorage.setItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());
      localStorage.setItem('cookie_preferences', JSON.stringify(prefs));

      // Update analytics consent
      setAnalyticsConsent(prefs.analytics);

      // Call callback
      if (onConsent) {
        onConsent(prefs);
      }

      setIsVisible(false);
    },
    [onConsent]
  );

  const handleAcceptAll = useCallback(() => {
    const allAccepted: ConsentPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    saveConsent('accepted', allAccepted);
  }, [saveConsent]);

  // Keeping for future use when decline button is added
  const _handleDeclineAll = useCallback(() => {
    const onlyNecessary: ConsentPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    saveConsent('declined', onlyNecessary);
  }, [saveConsent]);
  void _handleDeclineAll; // Prevent unused variable error

  const handleSavePreferences = useCallback(() => {
    saveConsent('custom', preferences);
    setShowSettings(false);
  }, [preferences, saveConsent]);

  const handlePreferenceChange = (key: keyof ConsentPreferences) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Position styles
  const positionStyles = {
    bottom: 'bottom-0 left-0 right-0',
    'bottom-left': 'bottom-4 left-4 max-w-md',
    'bottom-right': 'bottom-4 right-4 max-w-md',
    top: 'top-0 left-0 right-0',
  };

  // Auto-detect Cosmos pages for themed variant + correct privacy URL
  const isCosmosPage = location.pathname.startsWith('/cosmos');
  const isRealtorPage = location.pathname.startsWith('/cosmos/realtor');
  const effectivePrivacyUrl = privacyPolicyUrl;

  if (!isVisible) {
    return null;
  }

  // Realtor page variant — matches Energetic Professional color scheme
  if (isRealtorPage) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className={cn(
            'fixed z-50',
            positionStyles[position],
            className
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-consent-title"
        >
          <div className="bg-white border-t border-gray-200 px-4 py-4 sm:px-6 shadow-2xl">
            {/* Main Content */}
            <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Icon and Text */}
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-blue-800/10 rounded-lg flex-shrink-0">
                  <Cookie className="w-5 h-5 text-blue-800" aria-hidden="true" />
                </div>
                <div>
                  <h2 id="cookie-consent-title" className="text-sm font-semibold text-gray-800">
                    We value your privacy
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    We use cookies to enhance your experience and analyze site traffic.
                    {' '}
                    <a
                      href={cookiePolicyUrl || effectivePrivacyUrl}
                      className="text-blue-800 hover:text-blue-900 underline"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                {showPreferencesButton && (
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:border-blue-800/50 transition-colors"
                    aria-expanded={showSettings}
                  >
                    <Settings className="w-4 h-4" />
                    Customize
                  </button>
                )}
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all"
                  style={{ background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)', boxShadow: '0 0 20px rgba(249, 115, 22, 0.25)' }}
                >
                  Accept All
                </button>
              </div>
            </div>

            {/* Settings Panel */}
            <AnimatePresence>
              {showSettings && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="max-w-6xl mx-auto mt-4 pt-4 border-t border-gray-200">
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <CookieCategoryRealtor
                        title="Necessary"
                        description="Essential for the website to function"
                        checked={preferences.necessary}
                        disabled
                        onChange={() => {}}
                      />
                      <CookieCategoryRealtor
                        title="Analytics"
                        description="Help us understand how visitors interact"
                        checked={preferences.analytics}
                        onChange={() => handlePreferenceChange('analytics')}
                      />
                      <CookieCategoryRealtor
                        title="Marketing"
                        description="Used for targeted advertising"
                        checked={preferences.marketing}
                        onChange={() => handlePreferenceChange('marketing')}
                      />
                      <CookieCategoryRealtor
                        title="Preferences"
                        description="Remember your settings and preferences"
                        checked={preferences.preferences}
                        onChange={() => handlePreferenceChange('preferences')}
                      />
                    </div>

                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={handleSavePreferences}
                        className="px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all"
                        style={{ background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)' }}
                      >
                        Save Preferences
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Cosmos dark theme variant
  if (isCosmosPage) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className={cn(
            'fixed z-50',
            positionStyles[position],
            className
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-consent-title"
        >
          <div className="bg-[#0f0e1a] border-t border-white/10 px-4 py-4 sm:px-6 shadow-2xl">
            {/* Main Content */}
            <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Icon and Text */}
              <div className="flex items-start gap-3 flex-1">
                <div className="p-2 bg-violet-500/20 rounded-lg flex-shrink-0">
                  <Cookie className="w-5 h-5 text-violet-400" aria-hidden="true" />
                </div>
                <div>
                  <h2 id="cookie-consent-title" className="text-sm font-semibold text-white">
                    We value your privacy
                  </h2>
                  <p className="text-xs text-gray-400 mt-1">
                    We use cookies to enhance your experience and analyze site traffic.
                    {' '}
                    <a
                      href={cookiePolicyUrl || effectivePrivacyUrl}
                      className="text-violet-400 hover:text-violet-300 underline"
                    >
                      Learn more
                    </a>
                  </p>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                {showPreferencesButton && (
                  <button
                    onClick={() => setShowSettings(!showSettings)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-white/10 rounded-lg hover:border-violet-500/50 transition-colors"
                    aria-expanded={showSettings}
                  >
                    <Settings className="w-4 h-4" />
                    Customize
                  </button>
                )}
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-purple-600 rounded-lg hover:from-violet-500 hover:to-purple-500 shadow-[0_0_20px_rgba(124,58,237,0.3)] hover:shadow-[0_0_30px_rgba(124,58,237,0.5)] transition-all"
                >
                  Accept All
                </button>
              </div>
            </div>

            {/* Settings Panel */}
            <AnimatePresence>
              {showSettings && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="max-w-6xl mx-auto mt-4 pt-4 border-t border-white/10">
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      {/* Necessary Cookies */}
                      <CookieCategoryCosmos
                        title="Necessary"
                        description="Essential for the website to function"
                        checked={preferences.necessary}
                        disabled
                        onChange={() => {}}
                      />

                      {/* Analytics Cookies */}
                      <CookieCategoryCosmos
                        title="Analytics"
                        description="Help us understand how visitors interact"
                        checked={preferences.analytics}
                        onChange={() => handlePreferenceChange('analytics')}
                      />

                      {/* Marketing Cookies */}
                      <CookieCategoryCosmos
                        title="Marketing"
                        description="Used for targeted advertising"
                        checked={preferences.marketing}
                        onChange={() => handlePreferenceChange('marketing')}
                      />

                      {/* Preferences Cookies */}
                      <CookieCategoryCosmos
                        title="Preferences"
                        description="Remember your settings and preferences"
                        checked={preferences.preferences}
                        onChange={() => handlePreferenceChange('preferences')}
                      />
                    </div>

                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={handleSavePreferences}
                        className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-purple-600 rounded-lg hover:from-violet-500 hover:to-purple-500 transition-all"
                      >
                        Save Preferences
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Default main-site theme (near-black surface, amber accent)
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: position.includes('top') ? -100 : 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: position.includes('top') ? -100 : 100 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className={cn(
          'fixed z-50',
          positionStyles[position],
          className
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title"
      >
        <div className="bg-bg-surface border-t border-border-subtle px-4 py-4 sm:px-6 shadow-2xl">
          {/* Main Content */}
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Icon and Text */}
            <div className="flex items-start gap-3 flex-1">
              <div className="p-2 bg-accent-amber/10 rounded-lg flex-shrink-0">
                <Cookie className="w-5 h-5 text-accent-amber" aria-hidden="true" />
              </div>
              <div>
                <h2 id="cookie-consent-title" className="text-sm font-semibold text-text-primary font-display">
                  We value your privacy
                </h2>
                <p className="text-xs text-text-secondary mt-1">
                  We use cookies to enhance your experience, analyze site traffic, and for marketing purposes.
                  {' '}
                  <a
                    href={cookiePolicyUrl || effectivePrivacyUrl}
                    className="text-accent-amber hover:text-text-primary underline"
                  >
                    Learn more
                  </a>
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              {showPreferencesButton && (
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary border border-border-subtle rounded-lg hover:border-accent-amber/30 transition-colors"
                  aria-expanded={showSettings}
                >
                  <Settings className="w-4 h-4" />
                  Customize
                </button>
              )}
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm font-semibold border border-accent-amber/30 bg-accent-amber/10 text-accent-amber rounded-lg hover:bg-accent-amber/20 transition-colors"
              >
                Accept All
              </button>
            </div>
          </div>

          {/* Settings Panel */}
          <AnimatePresence>
            {showSettings && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="max-w-6xl mx-auto mt-4 pt-4 border-t border-border-subtle">
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {/* Necessary Cookies */}
                    <CookieCategoryDefault
                      title="Necessary"
                      description="Essential for the website to function"
                      checked={preferences.necessary}
                      disabled
                      onChange={() => {}}
                    />

                    {/* Analytics Cookies */}
                    <CookieCategoryDefault
                      title="Analytics"
                      description="Help us understand how visitors interact"
                      checked={preferences.analytics}
                      onChange={() => handlePreferenceChange('analytics')}
                    />

                    {/* Marketing Cookies */}
                    <CookieCategoryDefault
                      title="Marketing"
                      description="Used for targeted advertising"
                      checked={preferences.marketing}
                      onChange={() => handlePreferenceChange('marketing')}
                    />

                    {/* Preferences Cookies */}
                    <CookieCategoryDefault
                      title="Preferences"
                      description="Remember your settings and preferences"
                      checked={preferences.preferences}
                      onChange={() => handlePreferenceChange('preferences')}
                    />
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={handleSavePreferences}
                      className="px-4 py-2 text-sm font-semibold border border-accent-amber/30 bg-accent-amber/10 text-accent-amber rounded-lg hover:bg-accent-amber/20 transition-colors"
                    >
                      Save Preferences
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

interface CookieCategoryProps {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
}

/**
 * Cosmos-themed cookie category checkbox
 */
function CookieCategoryCosmos({
  title,
  description,
  checked,
  disabled = false,
  onChange,
}: CookieCategoryProps): JSX.Element {
  const id = `cookie-cosmos-${title.toLowerCase()}`;

  return (
    <div className="flex items-start gap-3">
      <div className="pt-0.5">
        <input
          type="checkbox"
          id={id}
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          className={cn(
            'h-4 w-4 rounded border-white/20 bg-white/5 text-violet-500',
            'focus:ring-violet-500 focus:ring-offset-0 focus:ring-offset-transparent',
            'checked:bg-violet-500 checked:border-violet-500',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        />
      </div>
      <label htmlFor={id} className={cn(disabled && 'cursor-not-allowed')}>
        <span className="text-xs font-medium text-white block">
          {title}
          {disabled && ' (Required)'}
        </span>
        <span className="text-xs text-gray-500">{description}</span>
      </label>
    </div>
  );
}

/**
 * Realtor-themed cookie category checkbox — matches Energetic Professional scheme
 */
function CookieCategoryRealtor({
  title,
  description,
  checked,
  disabled = false,
  onChange,
}: CookieCategoryProps): JSX.Element {
  const id = `cookie-realtor-${title.toLowerCase()}`;

  return (
    <div className="flex items-start gap-3">
      <div className="pt-0.5">
        <input
          type="checkbox"
          id={id}
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          className={cn(
            'h-4 w-4 rounded border-gray-300 text-orange-500',
            'focus:ring-orange-500 focus:ring-offset-0',
            'checked:bg-orange-500 checked:border-orange-500',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        />
      </div>
      <label htmlFor={id} className={cn(disabled && 'cursor-not-allowed')}>
        <span className="text-xs font-medium text-gray-800 block">
          {title}
          {disabled && ' (Required)'}
        </span>
        <span className="text-xs text-gray-500">{description}</span>
      </label>
    </div>
  );
}

/**
 * Main-site cookie category checkbox (amber accent) for kelleysd.com pages
 */
function CookieCategoryDefault({
  title,
  description,
  checked,
  disabled = false,
  onChange,
}: CookieCategoryProps): JSX.Element {
  const id = `cookie-default-${title.toLowerCase()}`;

  return (
    <div className="flex items-start gap-3">
      <div className="pt-0.5">
        <input
          type="checkbox"
          id={id}
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          className={cn(
            'h-4 w-4 rounded border-border-subtle bg-bg-primary text-accent-amber accent-accent-amber',
            'focus:ring-accent-amber focus:ring-offset-0 focus:ring-offset-transparent',
            'checked:bg-accent-amber checked:border-accent-amber',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        />
      </div>
      <label htmlFor={id} className={cn(disabled && 'cursor-not-allowed')}>
        <span className="text-xs font-medium text-text-primary block">
          {title}
          {disabled && ' (Required)'}
        </span>
        <span className="text-xs text-text-muted">{description}</span>
      </label>
    </div>
  );
}

/**
 * Hook to manage cookie consent programmatically
 */
export function useCookieConsent() {
  const [status, setStatus] = useState<ConsentStatus>('pending');
  const [preferences, setPreferences] = useState<ConsentPreferences | null>(null);

  useEffect(() => {
    const savedStatus = localStorage.getItem(CONSENT_KEY) as ConsentStatus | null;
    const savedPrefs = localStorage.getItem('cookie_preferences');

    if (savedStatus) {
      setStatus(savedStatus);
    }
    if (savedPrefs) {
      setPreferences(JSON.parse(savedPrefs));
    }
  }, []);

  const updateConsent = (newPrefs: Partial<ConsentPreferences>) => {
    const updatedPrefs: ConsentPreferences = {
      necessary: true,
      analytics: newPrefs.analytics ?? preferences?.analytics ?? false,
      marketing: newPrefs.marketing ?? preferences?.marketing ?? false,
      preferences: newPrefs.preferences ?? preferences?.preferences ?? false,
    };

    localStorage.setItem('cookie_preferences', JSON.stringify(updatedPrefs));
    localStorage.setItem(CONSENT_KEY, 'custom');
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());

    setPreferences(updatedPrefs);
    setStatus('custom');
    setAnalyticsConsent(updatedPrefs.analytics);
  };

  const hasConsented = status !== 'pending';
  const hasAnalytics = hasAnalyticsConsent();

  return {
    status,
    preferences,
    hasConsented,
    hasAnalytics,
    updateConsent,
  };
}

export default CookieConsent;
